import { useState, useEffect, useRef, useMemo } from 'react';
import { apiGet } from './apiClient';
import { indexerTradeToMarketTrade } from './sidebarTradeTypes';
import type { IndexerTradeItem } from './sidebarTradeTypes';
import type { MarketTrade } from './useMarketTrades';

/** How many of a selected trader's newest trades to pull into the trades table. */
const RECENT_LIMIT = 50;

interface TradesResponse {
    event_id: string;
    market_ids: string[];
    trades: IndexerTradeItem[];
    trade_count: number;
    has_more: boolean;
}

export interface SelectedTraderRecentTrades {
    /** Recent trades for the currently-highlighted accounts, flattened. */
    trades: MarketTrade[];
    /** True while a recent-trades fetch for any highlighted account is in flight,
     *  so the sidebar can show a spinner instead of an empty state. */
    loading: boolean;
}

/**
 * Fetch each selected trader's most-recent trades across the whole event and expose
 * them as a flat list, so the trades sidebar can surface them even when the trader has
 * been dormant and their trades fall outside the loaded event-wide window.
 *
 * Driven by the `highlightedAccounts` selection state — the single point every selection
 * path funnels into — so any path that selects a trader triggers the fetch automatically.
 *
 * The query is account-scoped (`accounts={addr}`), so `RECENT_LIMIT` bounds that trader's
 * OWN newest trades; a dormant trader's full recent history (including months-old trades)
 * comes back. Markets are left unscoped, matching the event-wide "All" list.
 *
 * This is intentionally separate from `useTraderTradesCache` (which feeds the convergence
 * chart and gain badges with full per-market history) to keep those concerns untouched.
 */
export function useSelectedTraderRecentTrades(
    eventId: string | null,
    fromDate: string | null | undefined,
    toDate: string | null | undefined,
    highlightedAccounts: Set<string>
): SelectedTraderRecentTrades {
    const [tradesByAccount, setTradesByAccount] = useState<
        Map<string, MarketTrade[]>
    >(new Map());
    // Reactive set of accounts with an in-flight fetch (drives `loading`).
    const [loadingAccounts, setLoadingAccounts] = useState<Set<string>>(
        new Set()
    );
    const inflight = useRef(new Set<string>());
    const loaded = useRef(new Set<string>());
    // One controller per in-flight account fetch. `highlightedAccounts` is
    // routinely passed as a fresh (but content-equal) Set on every render, so
    // a single shared controller aborted at the top of the effect would cancel
    // requests that are still perfectly valid — abort scope is per-account
    // instead, only torn down on a real event change or unmount.
    const abortControllers = useRef(new Map<string, AbortController>());

    // Reset everything when the event OR the requested window changes — cached
    // trades belong to one event and one window. The window matters as much as the
    // event: `loaded` short-circuits the fetch below, so without clearing it a
    // trader loaded under one window would keep serving those rows under the next,
    // and the fetch would silently ignore its own from/to arguments.
    // We intentionally retain the per-account cache across de-selection (only the
    // output is filtered to highlighted accounts), so re-selecting a trader is free.
    const rangeKey = `${fromDate ?? ''}|${toDate ?? ''}`;
    useEffect(() => {
        for (const controller of abortControllers.current.values()) {
            controller.abort();
        }
        abortControllers.current.clear();
        setTradesByAccount(new Map());
        setLoadingAccounts(new Set());
        inflight.current.clear();
        loaded.current.clear();
    }, [eventId, rangeKey]);

    // Fetch recent trades for any highlighted account we haven't loaded yet.
    useEffect(() => {
        if (!eventId) return;
        for (const account of highlightedAccounts) {
            if (loaded.current.has(account) || inflight.current.has(account))
                continue;
            inflight.current.add(account);
            setLoadingAccounts(prev => new Set(prev).add(account));

            const controller = new AbortController();
            abortControllers.current.set(account, controller);

            const addr = account.replace(/^polymarket:/, '');
            const params = new URLSearchParams({
                accounts: addr,
                limit: String(RECENT_LIMIT),
            });
            if (fromDate) params.set('from_ts', fromDate);
            if (toDate) params.set('to_ts', toDate);

            apiGet<TradesResponse>(
                `/api/trades/event/${encodeURIComponent(eventId)}?${params}`,
                controller.signal
            )
                .then(res => {
                    if (controller.signal.aborted) return;
                    const trades = res.trades.map(indexerTradeToMarketTrade);
                    loaded.current.add(account);
                    setTradesByAccount(prev => {
                        const next = new Map(prev);
                        next.set(account, trades);
                        return next;
                    });
                })
                .catch(err => {
                    if (err?.name === 'AbortError' || controller.signal.aborted)
                        return;
                    console.error(
                        `Failed to load recent trades for ${account}:`,
                        err
                    );
                })
                .finally(() => {
                    // Only the current attempt may clear this account's state.
                    // A window change aborts and restarts the fetch; the old
                    // promise settles a microtask later and would otherwise
                    // clear the *new* attempt's loading flag, so `loading` goes
                    // false mid-flight and the sidebar shows an empty state
                    // before the rows arrive.
                    if (abortControllers.current.get(account) !== controller)
                        return;
                    abortControllers.current.delete(account);
                    inflight.current.delete(account);
                    setLoadingAccounts(prev => {
                        const next = new Set(prev);
                        next.delete(account);
                        return next;
                    });
                });
        }
    }, [eventId, highlightedAccounts, fromDate, toDate]);

    // Abort any in-flight fetches on unmount.
    useEffect(() => {
        const controllers = abortControllers.current;
        return () => {
            for (const controller of controllers.values()) {
                controller.abort();
            }
        };
    }, []);

    // Flatten only the currently-highlighted accounts, so de-selecting a trader
    // immediately drops their trades while keeping them cached for re-selection.
    const trades = useMemo(() => {
        const out: MarketTrade[] = [];
        for (const account of highlightedAccounts) {
            const accountTrades = tradesByAccount.get(account);
            if (accountTrades) out.push(...accountTrades);
        }
        return out;
    }, [highlightedAccounts, tradesByAccount]);

    // Loading while any *currently-highlighted* account is still fetching.
    const loading = useMemo(() => {
        for (const account of highlightedAccounts) {
            if (loadingAccounts.has(account)) return true;
        }
        return false;
    }, [highlightedAccounts, loadingAccounts]);

    return { trades, loading };
}
